/**
 * Learning Page
 *
 * Management interface for learning materials and assignments
 * with tabbed navigation, filtering, and CRUD operations.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Upload,
  Search,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Users,
  FolderOpen,
  Sparkles,
} from 'lucide-react';
import {
  useMaterials,
  useAssignments,
  useDeleteAssignment,
  type MaterialListOptions,
  type AssignmentListOptions,
} from '../../hooks/useLearning';
import { useOperatorLookup } from '../../hooks/useOperators';
import { useMaterialsLookup } from '../../hooks/useLearning';
import { MaterialCard } from './components/MaterialCard';
import { AssignmentsTable } from './components/AssignmentsTable';
import { MaterialUploadModal } from './components/MaterialUploadModal';
import { MaterialDetailModal } from './components/MaterialDetailModal';
import { AssignMaterialModal } from './components/AssignMaterialModal';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useMemo(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

type Tab = 'materials' | 'assignments';

export function LearningPage() {
  useDocumentTitle('Learning');
  // Tab state
  const [activeTab, setActiveTab] = useState<Tab>('materials');

  // Materials filter state
  const [materialsSearch, setMaterialsSearch] = useState('');
  const [materialTypeFilter, setMaterialTypeFilter] = useState<'all' | 'video' | 'document'>('all');
  const [materialsPage, setMaterialsPage] = useState(1);

  // Assignments filter state
  const [assignmentsStatusFilter, setAssignmentsStatusFilter] = useState<'all' | 'assigned' | 'in_progress' | 'completed'>('all');
  const [assignmentsOperatorFilter, setAssignmentsOperatorFilter] = useState('');
  const [assignmentsMaterialFilter, setAssignmentsMaterialFilter] = useState('');
  const [assignmentsPage, setAssignmentsPage] = useState(1);

  // Modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [assignModalData, setAssignModalData] = useState<{ materialId: string; materialTitle: string } | null>(null);

  // Debounced search
  const debouncedMaterialsSearch = useDebounce(materialsSearch, 300);

  // Build materials query options
  const materialsOptions = useMemo<MaterialListOptions>(() => {
    const options: MaterialListOptions = {
      page: materialsPage,
      limit: 12,
    };
    if (debouncedMaterialsSearch) options.search = debouncedMaterialsSearch;
    if (materialTypeFilter !== 'all') options.materialType = materialTypeFilter;
    return options;
  }, [materialsPage, debouncedMaterialsSearch, materialTypeFilter]);

  // Build assignments query options
  const assignmentsOptions = useMemo<AssignmentListOptions>(() => {
    const options: AssignmentListOptions = {
      page: assignmentsPage,
      limit: 10,
    };
    if (assignmentsStatusFilter !== 'all') options.status = assignmentsStatusFilter;
    if (assignmentsOperatorFilter) options.operatorId = assignmentsOperatorFilter;
    if (assignmentsMaterialFilter) options.materialId = assignmentsMaterialFilter;
    return options;
  }, [assignmentsPage, assignmentsStatusFilter, assignmentsOperatorFilter, assignmentsMaterialFilter]);

  // Fetch data
  const { data: materialsData, isLoading: loadingMaterials } = useMaterials(materialsOptions);
  const { data: assignmentsData, isLoading: loadingAssignments } = useAssignments(assignmentsOptions);
  const { data: operators } = useOperatorLookup();
  const { data: materialsLookup } = useMaterialsLookup();
  const deleteAssignment = useDeleteAssignment();

  // Derived values
  const materials = materialsData?.data ?? [];
  const materialsPagination = materialsData?.pagination;
  const materialsTotalPages = materialsPagination?.totalPages ?? 1;
  const materialsTotal = materialsPagination?.total ?? 0;

  const assignments = assignmentsData?.data ?? [];
  const assignmentsPagination = assignmentsData?.pagination;
  const assignmentsTotalPages = assignmentsPagination?.totalPages ?? 1;
  const assignmentsTotal = assignmentsPagination?.total ?? 0;

  // Handlers
  const handleClearMaterialsFilters = useCallback(() => {
    setMaterialsSearch('');
    setMaterialTypeFilter('all');
    setMaterialsPage(1);
  }, []);

  const handleClearAssignmentsFilters = useCallback(() => {
    setAssignmentsStatusFilter('all');
    setAssignmentsOperatorFilter('');
    setAssignmentsMaterialFilter('');
    setAssignmentsPage(1);
  }, []);

  const handleMaterialClick = (materialId: string) => {
    setSelectedMaterialId(materialId);
    setIsDetailModalOpen(true);
  };

  const handleOpenAssignModal = (materialId: string, materialTitle: string) => {
    setIsDetailModalOpen(false);
    setAssignModalData({ materialId, materialTitle });
  };

  const handleDeleteAssignment = async (id: string) => {
    try {
      await deleteAssignment.mutateAsync(id);
    } catch (error) {
      console.error('Delete assignment failed:', error);
    }
  };

  const hasMaterialsFilters = Boolean(materialsSearch || materialTypeFilter !== 'all');
  const hasAssignmentsFilters = Boolean(
    assignmentsStatusFilter !== 'all' ||
    assignmentsOperatorFilter ||
    assignmentsMaterialFilter
  );

  // Generate page numbers
  const getPageNumbers = (current: number, total: number) => {
    const pages: (number | 'ellipsis')[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push('ellipsis');
      for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
        pages.push(i);
      }
      if (current < total - 2) pages.push('ellipsis');
      pages.push(total);
    }
    return pages;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/20">
              <BookOpen size={20} className="text-white" strokeWidth={2} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Learning
            </h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Manage training materials and operator assignments
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/learning/generated"
            className="inline-flex items-center gap-2 rounded-xl border border-purple-200 bg-purple-50 px-4 py-2.5 text-sm font-semibold text-purple-700 transition-all hover:bg-purple-100 hover:border-purple-300"
          >
            <Sparkles size={18} />
            AI Generated
          </Link>
          {activeTab === 'materials' && (
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-guild-green px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-guild-green/25 transition-all hover:bg-guild-green/90 hover:shadow-xl hover:shadow-guild-green/30"
            >
              <Upload size={18} />
              Upload Material
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-8">
          <button
            onClick={() => setActiveTab('materials')}
            className={`flex items-center gap-2 border-b-2 pb-4 pt-2 text-sm font-semibold transition-colors ${
              activeTab === 'materials'
                ? 'border-guild-green text-guild-green'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            <FolderOpen size={18} />
            Materials
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                activeTab === 'materials'
                  ? 'bg-guild-green/10 text-guild-green'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {materialsTotal}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`flex items-center gap-2 border-b-2 pb-4 pt-2 text-sm font-semibold transition-colors ${
              activeTab === 'assignments'
                ? 'border-guild-green text-guild-green'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            <Users size={18} />
            Assignments
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                activeTab === 'assignments'
                  ? 'bg-guild-green/10 text-guild-green'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {assignmentsTotal}
            </span>
          </button>
        </nav>
      </div>

      {/* Materials Tab */}
      {activeTab === 'materials' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search materials..."
                value={materialsSearch}
                onChange={(e) => {
                  setMaterialsSearch(e.target.value);
                  setMaterialsPage(1);
                }}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-11 pr-4 text-sm transition-colors focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
              />
            </div>

            {/* Type Filter */}
            <div className="relative">
              <Filter
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <select
                value={materialTypeFilter}
                onChange={(e) => {
                  setMaterialTypeFilter(e.target.value as 'all' | 'video' | 'document');
                  setMaterialsPage(1);
                }}
                className="appearance-none rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-10 text-sm font-medium text-gray-700 transition-colors focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
              >
                <option value="all">All Types</option>
                <option value="video">Videos</option>
                <option value="document">Documents</option>
              </select>
            </div>

            {/* Clear Filters */}
            {hasMaterialsFilters && (
              <button
                onClick={handleClearMaterialsFilters}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
              >
                <X size={14} />
                Clear filters
              </button>
            )}
          </div>

          {/* Materials Grid */}
          {loadingMaterials ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-guild-green" />
            </div>
          ) : materials.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <FolderOpen size={28} className="text-gray-400" />
              </div>
              <p className="mb-1 text-lg font-medium text-gray-900">No materials yet</p>
              <p className="mb-4 text-sm text-gray-500">
                Upload your first training material to get started
              </p>
              <button
                onClick={() => setIsUploadModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-guild-green px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-guild-green/90"
              >
                <Upload size={16} />
                Upload Material
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {materials.map((material) => (
                  <MaterialCard
                    key={material.id}
                    material={material}
                    onClick={() => handleMaterialClick(material.id)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {materialsTotalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                  <p className="text-sm text-gray-500">
                    Showing {(materialsPage - 1) * 12 + 1} to {Math.min(materialsPage * 12, materialsTotal)} of {materialsTotal} materials
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setMaterialsPage((p) => Math.max(1, p - 1))}
                      disabled={materialsPage === 1}
                      className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    {getPageNumbers(materialsPage, materialsTotalPages).map((pageNum, idx) =>
                      pageNum === 'ellipsis' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          onClick={() => setMaterialsPage(pageNum)}
                          className={`min-w-[36px] rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                            materialsPage === pageNum
                              ? 'bg-guild-green text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => setMaterialsPage((p) => Math.min(materialsTotalPages, p + 1))}
                      disabled={materialsPage === materialsTotalPages}
                      className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Assignments Tab */}
      {activeTab === 'assignments' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <select
              value={assignmentsStatusFilter}
              onChange={(e) => {
                setAssignmentsStatusFilter(e.target.value as 'all' | 'assigned' | 'in_progress' | 'completed');
                setAssignmentsPage(1);
              }}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
            >
              <option value="all">All Status</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>

            {/* Operator Filter */}
            <select
              value={assignmentsOperatorFilter}
              onChange={(e) => {
                setAssignmentsOperatorFilter(e.target.value);
                setAssignmentsPage(1);
              }}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
            >
              <option value="">All Operators</option>
              {operators?.map((op) => (
                <option key={op.id} value={op.id}>
                  {op.fullName}
                </option>
              ))}
            </select>

            {/* Material Filter */}
            <select
              value={assignmentsMaterialFilter}
              onChange={(e) => {
                setAssignmentsMaterialFilter(e.target.value);
                setAssignmentsPage(1);
              }}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
            >
              <option value="">All Materials</option>
              {materialsLookup?.map((mat) => (
                <option key={mat.id} value={mat.id}>
                  {mat.title}
                </option>
              ))}
            </select>

            {/* Clear Filters */}
            {hasAssignmentsFilters && (
              <button
                onClick={handleClearAssignmentsFilters}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
              >
                <X size={14} />
                Clear filters
              </button>
            )}
          </div>

          {/* Assignments Table */}
          <AssignmentsTable
            assignments={assignments}
            isLoading={loadingAssignments}
            onDelete={handleDeleteAssignment}
            isDeleting={deleteAssignment.isPending}
          />

          {/* Pagination */}
          {assignmentsTotalPages > 1 && !loadingAssignments && assignments.length > 0 && (
            <div className="flex items-center justify-between border-t border-gray-100 pt-4">
              <p className="text-sm text-gray-500">
                Showing {(assignmentsPage - 1) * 10 + 1} to {Math.min(assignmentsPage * 10, assignmentsTotal)} of {assignmentsTotal} assignments
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setAssignmentsPage((p) => Math.max(1, p - 1))}
                  disabled={assignmentsPage === 1}
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                >
                  <ChevronLeft size={18} />
                </button>
                {getPageNumbers(assignmentsPage, assignmentsTotalPages).map((pageNum, idx) =>
                  pageNum === 'ellipsis' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                      ...
                    </span>
                  ) : (
                    <button
                      key={pageNum}
                      onClick={() => setAssignmentsPage(pageNum)}
                      className={`min-w-[36px] rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                        assignmentsPage === pageNum
                          ? 'bg-guild-green text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                )}
                <button
                  onClick={() => setAssignmentsPage((p) => Math.min(assignmentsTotalPages, p + 1))}
                  disabled={assignmentsPage === assignmentsTotalPages}
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <MaterialUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      />

      <MaterialDetailModal
        materialId={selectedMaterialId}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedMaterialId(null);
        }}
        onAssign={handleOpenAssignModal}
      />

      <AssignMaterialModal
        materialId={assignModalData?.materialId ?? null}
        materialTitle={assignModalData?.materialTitle ?? ''}
        isOpen={!!assignModalData}
        onClose={() => setAssignModalData(null)}
      />
    </div>
  );
}
